// Point d'entrée : authentification, coquille de l'application et routage.

// ⚠️ CES DEUX IMPORTS DOIVENT RESTER EN PREMIER.
// Ils posent window.APP et window.FIREBASE_CONFIG, que store.js lit dès son
// évaluation. L'ordre des modules ES est celui des imports : les mettre ailleurs
// ferait démarrer l'application sans savoir à quelle base elle parle.
import { APP } from "./config.js";
import "./firebase-config.js";

import Store, { reglerHorloge } from "./store.js";
import { $, esc, avatar, toast } from "./ui.js";
import { jouer } from "./sons.js";
import { icone, iconeDuo } from "./icones.js";
import * as Alertes from "./alertes.js";
import { creerSignaux } from "./signaux.js";
import { accueillir } from "./decouverte.js";
import * as SalleAttente from "./salle-attente.js";
import { doitAttendre } from "./salle-attente.js";
import { tirerPourRafraichir, retourAuToucher } from "./gestes.js";
import { demarrerAnimations, animerEcran } from "./animations.js";
import * as VueConnexion from "./views/connexion.js";
import * as VueFil from "./views/fil.js";
import * as VueMembres from "./views/membres.js";
import * as VueMessages from "./views/messages.js";
import * as VueProfil from "./views/profil.js";
import * as VueNotifs from "./views/notifications.js";
import * as VueRecherche from "./views/recherche.js";
import * as VuePublication from "./views/publication.js";
import * as VueReels from "./views/reels.js";
import * as VueModeration from "./views/moderation.js";
import * as VueGroupes from "./views/groupes.js";
import * as VueEvenements from "./views/evenements.js";
import * as VueEvenement from "./views/evenement.js";
import * as VueGroupe from "./views/groupe.js";
import * as VuePages from "./views/pages.js";
import * as VuePage from "./views/page.js";
import * as VueSponsoring from "./views/sponsoring.js";
import * as VueMenu from "./views/menu.js";
import * as VueParametres from "./views/parametres.js";
import { estSuspendu } from "./views/moderation.js";

const A = APP;
document.title = `${A.nom} — ${A.slogan}`;

/* ------------------------------------------------------------------- contexte */

const ecouteursAmens = new Set();
const ecouteursStatuts = new Set();
const cacheProfils = new Map();

const ctx = {
  moi: null,
  // Une CARTE `postId → réaction`, et non plus un ensemble : depuis les six
  // réactions de plus, savoir QUE j'ai réagi ne suffit plus, il faut savoir
  // laquelle. `Map` garde `.has()`, donc rien de ce qui l'interrogeait n'a
  // eu à changer.
  amens: new Map(),
  onAmens(cb) {
    ecouteursAmens.add(cb);
    return () => ecouteursAmens.delete(cb);
  },
  // Qui a un statut en cours. Sert à cercler son avatar de bleu, partout où il
  // apparaît — comme sur Facebook. Une seule écoute pour toute l'application.
  avecStatut: new Set(),
  onStatuts(cb) {
    ecouteursStatuts.add(cb);
    return () => ecouteursStatuts.delete(cb);
  },
  // Après modification de son propre profil : rafraîchit l'entête et la page courante.
  majMoi(patch) {
    if (!ctx.moi) return;
    Object.assign(ctx.moi, patch);
    coquille();
    majBadgeMessages();
    router();
  },
  async profil(uid) {
    if (uid === ctx.moi?.uid) return ctx.moi;
    if (!cacheProfils.has(uid)) cacheProfils.set(uid, Store.users.get(uid).catch(() => null));
    return cacheProfils.get(uid);
  },
  // Le collecteur qui alimente les algorithmes de classement. Un seul par
  // session : les écrans lui demandent un instantané, ils ne s'abonnent pas
  // chacun de leur côté aux mêmes données.
  signaux: null,
};

/* -------------------------------------------------------------------- routage */

const ROUTES = [
  { motif: /^#\/fil(?:\/(\w+))?$/, vue: VueFil, params: (m) => ({ filtre: m[1] }), onglet: "fil" },
  { motif: /^#\/reels$/, vue: VueReels, params: () => ({}), onglet: "reels" },
  { motif: /^#\/membres$/, vue: VueMembres, params: () => ({}), onglet: "membres" },
  { motif: /^#\/groupes$/, vue: VueGroupes, params: () => ({}), onglet: "menu" },
  { motif: /^#\/pages$/, vue: VuePages, params: () => ({}), onglet: "menu" },
  { motif: /^#\/evenements$/, vue: VueEvenements, params: () => ({}), onglet: "menu" },
  { motif: /^#\/evenement\/(.+)$/, vue: VueEvenement, params: (m) => ({ id: m[1] }), onglet: "menu" },
  { motif: /^#\/sponsoring$/, vue: VueSponsoring, params: () => ({}), onglet: "menu" },
  { motif: /^#\/parametres$/, vue: VueParametres, params: () => ({}), onglet: "menu" },
  { motif: /^#\/menu$/, vue: VueMenu, params: () => ({}), onglet: "menu" },
  { motif: /^#\/page\/(.+)$/, vue: VuePage, params: (m) => ({ id: m[1] }), onglet: "menu" },
  { motif: /^#\/groupe\/(.+)$/, vue: VueGroupe, params: (m) => ({ id: m[1] }), onglet: "menu" },
  { motif: /^#\/messages(?:\/(.+))?$/, vue: VueMessages, params: (m) => ({ id: m[1] }), onglet: "messages" },
  { motif: /^#\/profil(?:\/(.+))?$/, vue: VueProfil, params: (m) => ({ id: m[1] }), onglet: "profil" },
  { motif: /^#\/notifications$/, vue: VueNotifs, params: () => ({}), onglet: "notifications" },
  { motif: /^#\/moderation$/, vue: VueModeration, params: () => ({}), onglet: "menu" },
  { motif: /^#\/recherche(?:\/(.*))?$/, vue: VueRecherche, params: (m) => ({ q: m[1] }), onglet: "recherche" },
  { motif: /^#\/publication\/(.+)$/, vue: VuePublication, params: (m) => ({ id: m[1] }), onglet: "fil" },
];

/* ⚠️ L'ONGLET COURANT EST GARDÉ EN MÉMOIRE, pas seulement posé sur le DOM.
   `coquille()` réécrit les deux barres d'un coup (innerHTML) : à chaque appel —
   changement de photo de profil, adresse confirmée — les liens repartent neufs
   et perdent leur `est-actif`. On voyait alors la barre s'éteindre : plus aucun
   onglet marqué, jusqu'au prochain changement de page. Depuis que le verre
   dépoli marque l'onglet courant, cet oubli se verrait tout de suite.
   `lienNav()` relit cette variable à la construction : la barre naît déjà juste. */
let ongletCourant = "fil";

let nettoyerVue = null;
// La page d'entrée est en React elle aussi : elle rend une fonction d'arrêt
// qu'il faut appeler, sinon React garderait une racine sur un nœud effacé.
let nettoyerAuth = null;
let generation = 0;

function router() {
  if (!ctx.moi) return;
  const hash = location.hash || "#/fil";
  const route = ROUTES.map((r) => ({ r, m: hash.match(r.motif) })).find((x) => x.m);
  if (!route) {
    location.hash = "#/fil";
    return;
  }
  if (nettoyerVue) nettoyerVue();
  const vue = $("#vue");
  vue.scrollTop = 0;
  window.scrollTo(0, 0);
  // jeton de génération : une vue qui charge en asynchrone doit abandonner
  // si l'utilisateur a déjà changé de page entre-temps
  vue.dataset.gen = String(++generation);
  nettoyerVue = route.r.vue.rendre(vue, ctx, route.r.params(route.m)) || null;
  animerEcran(vue); // l'écran glisse en place — voir js/animations.js

  ongletCourant = route.r.onglet;
  document.querySelectorAll("[data-onglet-nav]").forEach((a) =>
    a.classList.toggle("est-actif", a.dataset.ongletNav === route.r.onglet)
  );
}

/**
 * Retire l'écran de démarrage.
 *
 * Appelée dès que l'application SAIT quoi montrer — l'écran de connexion ou le
 * compte — jamais avant. Le membre ne voit donc aucun état intermédiaire : le
 * démarrage s'efface sur quelque chose de déjà dessiné.
 *
 * Deux images d'attente avant de retirer : sans elles, le fondu commence pendant
 * que le navigateur peint encore l'écran en dessous, et l'on aperçoit la page à
 * moitié construite — exactement ce qu'on cherchait à cacher.
 *
 * Le nœud est retiré du document à la fin. Le laisser, même invisible, c'est
 * garder une couche à plein écran au-dessus de tout le reste : le jour où un
 * style la réveille, plus rien n'est cliquable et personne ne comprend pourquoi.
 */
let demarrageFini = false;
function finDuDemarrage() {
  if (demarrageFini) return;
  demarrageFini = true;
  const ecran = document.getElementById("demarrage");
  if (!ecran) return;
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      ecran.classList.add("est-fini");
      setTimeout(() => ecran.remove(), 450);
    })
  );
}

/* ⚠️ L'HEURE EST RECALÉE AU DÉMARRAGE, ET SANS ATTENDRE. Tout ce qui s'écrit
   est daté par l'appareil puis TRIÉ sur cette date chez tout le monde : une
   horloge en avance range vos messages après les réponses qu'ils ont
   provoquées. On demande donc l'écart à un serveur, une fois par session.

   ⚠️ SANS `await` : l'application ne doit pas attendre le réseau pour
   s'ouvrir. Les premières seconde ou deux se datent sur l'horloge de
   l'appareil — ce qui était le cas de TOUT jusqu'ici. Voir `reglerHorloge`. */
reglerHorloge(APP.urlPousser);

// L'âme animée : une fois, et seulement si l'appareil et le membre le veulent.
demarrerAnimations();

window.addEventListener("hashchange", router);

/* ------------------------------------------------------------------- coquille */

function coquille() {
  $("#barre-haut").innerHTML = `
    <a class="logo" href="#/fil"><img class="logo__marque" src="icons/icon-192.png?v=2" alt="" width="34" height="34"><span class="logo__texte">${esc(A.nom)}</span></a>
    <form class="recherche" id="form-recherche" role="search">
      <span class="recherche__loupe">${icone("recherche", { taille: 18 })}</span>
      <input type="search" id="champ-recherche" placeholder="Rechercher sur ${esc(A.nom)}" aria-label="Rechercher" autocomplete="off">
    </form>
    <nav class="nav-large">
      ${lienNav("fil", "fil", "Fil", "#/fil")}
      ${lienNav("reels", "reels", "Reels", "#/reels")}
      ${lienNav("membres", "membres", "Membres", "#/membres")}
      ${lienNav("messages", "messages", "Messages", "#/messages")}
      ${lienNav("menu", "menu", "Menu", "#/menu")}
    </nav>
    ${lienApplication()}
    <a class="lien-nav lien-nav--cloche${ongletCourant === "notifications" ? " est-actif" : ""}" data-onglet-nav="notifications" href="#/notifications" aria-label="Notifications">
      <span class="lien-nav__icone">${iconeDuo("alertes", 24)}<span class="badge" data-badge-notif hidden></span></span>
    </a>
    <a class="barre-haut__moi" href="#/profil" data-onglet-nav="profil" aria-label="Mon profil">${avatar(ctx.moi, 36)}</a>`;

  $("#barre-basse").innerHTML = `
    ${lienNav("fil", "fil", "Fil", "#/fil")}
    ${lienNav("reels", "reels", "Reels", "#/reels")}
    ${lienNav("membres", "membres", "Membres", "#/membres")}
    ${lienNav("messages", "messages", "Messages", "#/messages")}
    ${lienNav("notifications", "alertes", "Alertes", "#/notifications")}
    ${lienNav("menu", "menu", "Menu", "#/menu")}`;

  // bandeau permanent tant que le compte est suspendu : le membre doit comprendre
  // pourquoi ses publications sont refusées
  const banniere = $("#banniere");
  if (ctx.moi?.validation === "en_attente") {
    banniere.className = "banniere banniere--attente";
    /* ⚠️ « ATTESTATION » A DISPARU LE 18 AOÛT 2026 — ce bandeau en parlait
       encore le 20 octobre (audit). C'est l'INSCRIPTION qui est examinée.
       (Bandeau normalement inatteignable depuis que la salle d'attente retient
       `en_attente` ; on le garde juste, pour le jour où il resservirait.) */
    banniere.innerHTML = `<strong>Inscription en cours d'examen</strong> — vous pourrez publier
      dès qu'un responsable l'aura validée, sous ${A.delaiValidationHeures} heures.`;
    banniere.hidden = false;
  } else if (ctx.moi?.validation === "refuse") {
    banniere.className = "banniere";
    /* ⚠️ « ATTESTATION » A DISPARU LE 18 AOÛT 2026 — ce bandeau en parlait
       encore le 24. On ne demande plus de document : c'est l'inscription qui
       est examinée. */
    banniere.innerHTML = `<strong>Inscription non validée</strong> — écrivez-nous si vous pensez que c'est une erreur.
      ${ctx.moi.baptemeDecide?.motif ? `<span class="banniere__motif">${esc(ctx.moi.baptemeDecide.motif)}</span>` : ""}`;
    banniere.hidden = false;
  } else if (ctx.moi?.emailVerifie === false) {
    banniere.className = "banniere banniere--info";
    /* ⚠️ CE BANDEAU NE DEVRAIT PLUS PARAÎTRE — 24 août 2026. Depuis que
       `doitAttendre` retient l'état `email`, un compte non confirmé est en
       salle d'attente, pas dans le fil. Il ne reste ici que pour les comptes
       anciens, validés jadis sans que leur adresse l'ait été.

       ⚠️ ET IL PARLAIT ENCORE D'UN LIEN. La confirmation se fait par un CODE
       à six chiffres depuis le 23 août ; « ouvrez le lien reçu par e-mail »
       envoyait donc chercher un message qui n'existe plus. */
    banniere.innerHTML = `<strong>Confirmez votre adresse</strong> — un code à six chiffres
      vous attend pour ${esc(ctx.moi.email || "votre adresse")}. Vous pourrez publier
      dès qu'elle sera confirmée.
      <span class="banniere__actions">
        <button class="btn btn--petit" data-renvoyer>Recevoir mon code</button>
        <button class="btn btn--petit" data-verifier>J'ai confirmé</button>
      </span>`;
    banniere.hidden = false;
    banniere.querySelector("[data-renvoyer]").onclick = async (e) => {
      e.target.disabled = true;
      try {
        await Store.auth.demanderCodeEmail();
        toast("Code envoyé — regardez votre boîte, et vos indésirables");
      } catch (err) {
        toast(err.message || "Envoi impossible", "erreur");
      }
      setTimeout(() => (e.target.disabled = false), 30000);
    };
    banniere.querySelector("[data-verifier]").onclick = async () => {
      const ok = await Store.auth.rafraichirVerification();
      if (ok) {
        ctx.moi.emailVerifie = true;
        coquille();
        toast("Adresse confirmée, bienvenue 🙌");
      } else toast("Pas encore confirmée. Saisissez le code reçu par e-mail.", "erreur");
    };
  } else if (estSuspendu(ctx.moi)) {
    banniere.className = "banniere";
    const fin = ctx.moi.suspendu;
    const definitive = fin > Date.now() + 3650 * 864e5;
    banniere.innerHTML = `<strong>Compte suspendu</strong> — vous pouvez lire la communauté mais pas publier
      ${definitive ? "" : `jusqu'au ${esc(new Date(fin).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }))}`}.
      ${ctx.moi.suspenduMotif ? `<span class="banniere__motif">${esc(ctx.moi.suspenduMotif)}</span>` : ""}`;
    banniere.hidden = false;
  } else {
    banniere.hidden = true;
    banniere.innerHTML = "";
  }


  // on mesure le bandeau après l'avoir rempli : sa hauteur dépend du texte
  requestAnimationFrame(() => {
    const h = banniere.hidden ? 0 : banniere.offsetHeight;
    document.documentElement.style.setProperty("--bandeau", h + "px");
  });

  const form = $("#form-recherche");
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const q = $("#champ-recherche").value.trim();
    location.hash = `#/recherche${q ? "/" + encodeURIComponent(q) : ""}`;
    $("#champ-recherche").blur();
  });
}

/**
 * Le bouton « Installer l'application », dans l'en-tête du compte.
 *
 * ⚠️ IL NE S'AFFICHE PAS DANS L'APPLICATION. Proposer d'installer l'application
 * à quelqu'un qui est déjà dedans est le genre de détail qui fait douter du
 * sérieux de tout le reste. On écarte donc la coquille Android (Capacitor) et
 * l'application déjà installée depuis l'écran d'accueil (`standalone`).
 *
 * ⚠️ ET PAS SUR IPHONE NON PLUS : il n'y a pas d'application iOS, et il n'y en
 * aura pas. Un bouton qui mène à un fichier qu'on ne peut pas ouvrir est pire
 * que pas de bouton — il donne le sentiment d'être laissé de côté. Le membre
 * sur iPhone utilise le site, qui fait tout, et la page de téléchargement le
 * lui dit si jamais il y arrive par un lien.
 */
function lienApplication() {
  if (window.Capacitor?.isNativePlatform?.()) return "";
  if (window.matchMedia?.("(display-mode: standalone)")?.matches) return "";
  if (/iPhone|iPad|iPod/.test(navigator.userAgent || "")) return "";
  return `<a class="lien-nav lien-nav--app" href="application.html"
             aria-label="Installer l'application Android" title="Installer l'application Android">
            <span class="lien-nav__icone">${icone("telecharger", { taille: 22 })}</span>
          </a>`;
}

const lienNav = (id, nomIcone, label, href) => `
  <a class="lien-nav${id === ongletCourant ? " est-actif" : ""}" data-onglet-nav="${id}" href="${href}">
    <span class="lien-nav__icone">${iconeDuo(nomIcone, 24)}${id === "messages" ? '<span class="badge" data-badge-msg hidden></span>' : ""}${id === "notifications" ? '<span class="badge" data-badge-notif hidden></span>' : ""}${id === "membres" ? '<span class="badge" data-badge-amis hidden></span>' : ""}${id === "menu" ? '<span class="badge" data-badge-menu hidden></span>' : ""}</span>
    <span class="lien-nav__label">${esc(label)}</span>
  </a>`;

let dernieresConvos = [];
let dernieresNotifs = [];
// Le nombre d'alertes non lues au tour précédent : c'est l'augmentation qui
// fait le son, jamais le simple fait d'avoir des alertes en attente.
let dernierCompteAlertes = 0;

function badge(selecteur, n) {
  document.querySelectorAll(selecteur).forEach((b) => {
    b.hidden = n === 0;
    b.textContent = n > 9 ? "9+" : String(n);
  });
}

// Ce que l'appareil a déjà annoncé, par conversation : son `dernierAt`.
// Sans cela, chaque réémission de l'abonnement — et il y en a à chaque frappe
// de l'autre — rejouerait la même alerte.
const messagesAnnonces = new Map();
let premiereListeConvos = true;

function majBadgeMessages(convos) {
  if (convos) dernieresConvos = convos;
  if (!ctx.moi) return;
  const nonLues = dernieresConvos.filter((c) => c.lu && c.lu[ctx.moi.uid] === false);
  badge("[data-badge-msg]", nonLues.length);

  if (!convos) return;

  // Au démarrage, l'abonnement rend tout l'historique : on l'enregistre comme
  // déjà connu, sinon revenir dans l'application déclencherait une alerte pour
  // chaque conversation en attente.
  if (premiereListeConvos) {
    premiereListeConvos = false;
    convos.forEach((c) => messagesAnnonces.set(c.id, c.dernierAt || 0));
    return;
  }

  /* Les messages n'écrivent plus de notification : ils n'encombrent donc plus la
     liste des alertes. Mais un message reçu téléphone éteint doit tout de même
     faire sonner l'appareil — c'est ici que ça se joue, en suivant directement
     les conversations plutôt qu'une notification écrite pour rien. */
  for (const c of nonLues) {
    const quand = c.dernierAt || 0;
    if (quand <= (messagesAnnonces.get(c.id) || 0)) continue;
    // marqué avant d'aller chercher le nom : le profil est asynchrone, et deux
    // émissions rapprochées ne doivent pas annoncer deux fois le même message
    messagesAnnonces.set(c.id, quand);
    /* ⚠️ LE SON D'ABORD, LE BANDEAU ENSUITE, ET PAS AUX MÊMES CONDITIONS.
       Le bandeau système ne sert QUE si l'on regarde ailleurs — d'où la ligne
       suivante. Le son, lui, sert surtout quand on EST devant l'écran : c'est
       ce qui fait lever les yeux vers la conversation. Les deux ne se
       remplacent donc pas, et l'ordre compte. */
    jouer("recu");
    if (!Alertes.activees() || document.visibilityState === "visible") continue;
    const autre = c.membres.find((m) => m !== ctx.moi.uid);
    ctx.profil(autre).then((u) =>
      Alertes.afficher(`${u?.nom || "Un membre"} vous a écrit`, c.dernier || "", `#/messages/${c.id}`)
    );
  }
}

let premiereListeNotifs = true;

// Un message n'est plus une notification (voir majBadgeMessages) : les rares
// qui restent dans la base ont été écrites avant ce changement, et elles ne
// doivent plus ni compter dans la pastille ni faire sonner l'appareil.
const estMessage = (n) => n.type === "message";

function majBadgeNotifs(notifs) {
  if (notifs) dernieresNotifs = notifs.filter((n) => !estMessage(n));
  if (!ctx.moi) return;
  badge("[data-badge-notif]", dernieresNotifs.filter((n) => !n.lu).length);

  if (!notifs) return;
  notifs = dernieresNotifs;
  if (premiereListeNotifs) {
    // au démarrage, l'abonnement renvoie tout l'historique : on l'enregistre
    // comme « déjà connu » pour ne pas noyer le membre d'alertes d'un coup
    premiereListeNotifs = false;
    Alertes.amorcer(notifs.map((n) => n.id));
    return;
  }
  /* Une alerte non lue de plus qu'au tour d'avant : quelque chose vient
     d'arriver. On compare les nombres plutôt que les identifiants — c'est ce
     que fait déjà la pastille juste au-dessus, et cela suffit à distinguer
     « il y a du neuf » de « la liste a simplement été relue ». */
  const nonLues = notifs.filter((n) => !n.lu).length;
  if (nonLues > dernierCompteAlertes) jouer("alerte");
  dernierCompteAlertes = nonLues;

  Alertes.signaler(notifs);
}

// pastille de l'onglet Menu : ce qui attend un modérateur
let signalementsEnAttente = 0;
let baptemesEnAttente = 0;

function majBadgeMenu() {
  badge("[data-badge-menu]", signalementsEnAttente + baptemesEnAttente);
}

let dernieresRelations = [];

function majBadgeAmis(relations) {
  if (relations) dernieresRelations = relations;
  if (!ctx.moi) return;
  badge("[data-badge-amis]", dernieresRelations.filter((r) => r.etat === "attente" && r.vers === ctx.moi.uid).length);
}

/* ----------------------------------------------------------------- présence */

// Signal de vie régulier : c'est ce qui alimente la pastille « en ligne ».
function demarrerPresence() {
  const battre = () => Store.presence.battre();
  battre();
  // 80 s, et non 40 : chaque battement est une écriture Firestore par membre
  // connecté — à 80 s on en paie moitié moins (20 octobre 2026, demandé par
  // Josaphat). Doit rester SOUS la moitié de FENETRE_PRESENCE (180 s, dans
  // js/store.js) pour qu'un battement manqué ne fasse pas clignoter le badge.
  const minuterie = setInterval(battre, 80000);
  const surVisibilite = () => !document.hidden && battre();
  document.addEventListener("visibilitychange", surVisibilite);
  const surFermeture = () => Store.presence.partir();
  window.addEventListener("pagehide", surFermeture);

  return () => {
    clearInterval(minuterie);
    document.removeEventListener("visibilitychange", surVisibilite);
    window.removeEventListener("pagehide", surFermeture);
    Store.presence.partir();
  };
}

/* ---------------------------------------------------------------- démarrage */

const abonnementsSession = [];

Store.auth.onChange((utilisateur) => {
  const changementDeCompte = utilisateur?.uid !== ctx.moi?.uid;
  ctx.moi = utilisateur;

  if (!utilisateur) {
    abonnementsSession.forEach((f) => f && f());
    abonnementsSession.length = 0;
    if (nettoyerVue) {
      nettoyerVue();
      nettoyerVue = null;
    }
    cacheProfils.clear();
    ctx.signaux?.detruire();
    ctx.signaux = null;
    $("#app").hidden = true;
    $("#auth").hidden = false;
    if (!nettoyerAuth) nettoyerAuth = VueConnexion.rendre($("#auth")) || null;
    finDuDemarrage();
    return;
  }

  /* ── LA PORTE ────────────────────────────────────────────────────────────
     Tant qu'un responsable n'a pas validé l'attestation, on n'entre pas : la
     salle d'attente prend la place de l'application, dans le même conteneur que
     l'écran de connexion — c'est déjà la surface du « pas encore dedans ».
     Elle est posée ICI, avant que quoi que ce soit de l'application ne soit
     dessiné ou abonné : rien de la communauté ne doit être demandé au serveur
     pour quelqu'un qui n'y est pas encore admis. Voir `js/salle-attente.js`. */
  if (doitAttendre(utilisateur)) {
    abonnementsSession.forEach((f) => f && f());
    abonnementsSession.length = 0;
    if (nettoyerVue) {
      nettoyerVue();
      nettoyerVue = null;
    }
    ctx.signaux?.detruire();
    ctx.signaux = null;
    if (nettoyerAuth) {
      nettoyerAuth();
      nettoyerAuth = null;
    }
    $("#app").hidden = true;
    $("#auth").hidden = false;
    nettoyerAuth = SalleAttente.rendre($("#auth"), utilisateur);
    finDuDemarrage();
    return;
  }

  if (nettoyerAuth) {
    nettoyerAuth();
    nettoyerAuth = null;
  }
  $("#auth").hidden = true;
  $("#auth").innerHTML = "";
  $("#app").hidden = false;
  coquille();
  majBadgeMessages();
  majBadgeNotifs();
  majBadgeMenu();
  majBadgeAmis();

  if (changementDeCompte) {
    abonnementsSession.forEach((f) => f && f());
    abonnementsSession.length = 0;
    ctx.signaux?.detruire();
    ctx.signaux = creerSignaux(utilisateur);
    ctx.signaux.rafraichirVoisinage();
    premiereListeNotifs = true;
    signalementsEnAttente = 0;
    baptemesEnAttente = 0;
    abonnementsSession.push(
      Store.amens.subscribeMine((carte) => {
        ctx.amens = carte;
        ecouteursAmens.forEach((f) => f(carte));
      }),
      Store.stories.subscribe((ss) => {
        ctx.avecStatut = new Set((ss || []).map((s) => s.authorId).filter(Boolean));
        ecouteursStatuts.forEach((f) => f(ctx.avecStatut));
      }),
      Store.convos.subscribe(majBadgeMessages),
      Store.notifs.subscribe(majBadgeNotifs),
      // La pastille du Menu ne compte que pour un modérateur. On ne s'abonne
      // même pas pour les autres : en mode local rien ne filtrerait, et un
      // simple membre verrait une pastille pour un travail qui ne le regarde pas.
      ...(["moderateur", "admin"].includes(utilisateur.role)
        ? [
            Store.reports.subscribe((l) => {
              signalementsEnAttente = l.filter((x) => !x.traite).length;
              majBadgeMenu();
            }),
            Store.moderation.subscribeAValider((l) => {
              baptemesEnAttente = l.length;
              majBadgeMenu();
            }),
          ]
        : []),
      Store.amis.subscribe((relations) => {
        majBadgeAmis(relations);
        // Le fil d'un nouveau venu ne doit pas être vide. On attend la liste
        // des amitiés plutôt que d'agir tout de suite : sans elle, on ne sait
        // pas s'il est vraiment seul. `accueillir` ne fait rien deux fois.
        accueillir(utilisateur, relations);
      }),
      demarrerPresence()
    );
    if (!location.hash || location.hash === "#/") location.hash = "#/fil";
    router();
    finDuDemarrage();

    /* Les gestes, une fois la session ouverte — pas avant : sur l'écran de
       connexion, tirer vers le bas ne doit rien recharger.

       Le rafraîchissement redessine l'écran COURANT, quel qu'il soit. C'est
       ce que fait Facebook : on tire, et ce qu'on regarde se remet à jour.
       `router()` refait exactement ce qu'il aurait fait à l'arrivée sur la
       page — aucun écran n'a besoin de prévoir quoi que ce soit pour en
       profiter, et un écran ajouté demain en héritera d'office. */
    retourAuToucher();
    tirerPourRafraichir(async () => {
      /* ⚠️ `rafraichirVoisinage`, PAS `rafraichir` — 20 octobre 2026. La
         méthode appelée ici n'a jamais existé sur l'objet de `creerSignaux`,
         et le `?.` avalait l'absence : tirer pour rafraîchir redessinait
         l'écran sans jamais rafraîchir les amis d'amis. Trouvé à l'audit. */
      ctx.signaux?.rafraichirVoisinage?.();
      router();
      // On laisse le temps aux abonnements de rendre leur première réponse :
      // rendre la main aussitôt ferait clignoter l'anneau sans rien montrer.
      await new Promise((r) => setTimeout(r, 420));
    });
  }
});

/* --------------------------------------------------- application Android */

// Dans l'app Android (Capacitor), le bouton « retour » du téléphone doit revenir
// en arrière dans l'app, et ne quitter que depuis l'accueil.
if (window.Capacitor?.isNativePlatform?.()) {
  document.documentElement.classList.add("est-app");
  window.Capacitor.Plugins?.App?.addListener("backButton", ({ canGoBack }) => {
    const accueil = ["#/fil", "#/", ""].includes(location.hash);
    if (!accueil && canGoBack) history.back();
    else if (!accueil) location.hash = "#/fil";
    else window.Capacitor.Plugins.App.exitApp();
  });
}

// alertes : sur Android, il faut relire l'autorisation et brancher le clic
Alertes.initialiser();

// Service worker (mode hors-ligne de la coquille)
if ("serviceWorker" in navigator && location.protocol.startsWith("http")) {
  window.addEventListener("load", () => navigator.serviceWorker.register("sw.js").catch(() => {}));
}

window.addEventListener("online", () => toast("Connexion rétablie"));
window.addEventListener("offline", () => toast("Vous êtes hors ligne", "erreur"));
